/**
 * FloatingTrustBadge.tsx
 * 중고폰 안심거래 인증 사업자 배지 - 화면 오른쪽 여백 중앙 고정
 */

import React, { useState, useEffect } from 'react';
import { Box, Typography, IconButton, useMediaQuery, useTheme } from '@mui/material';
import { Close } from '@mui/icons-material';
import { keyframes } from '@mui/system';

const STORAGE_KEY = 'trust_badge_dismissed';

const slideInDesktop = keyframes`
  0% { transform: translate(-50%, -50%) translateX(80px); opacity: 0; }
  70% { transform: translate(-50%, -50%) translateX(-3px); opacity: 1; }
  100% { transform: translate(-50%, -50%) translateX(0); opacity: 1; }
`;

const slideOutDesktop = keyframes`
  0% { transform: translate(-50%, -50%) translateX(0); opacity: 1; }
  100% { transform: translate(-50%, -50%) translateX(120px); opacity: 0; }
`;

const slideInMobile = keyframes`
  0% { transform: translateY(-50%) translateX(100%); opacity: 0; }
  70% { transform: translateY(-50%) translateX(-3px); opacity: 1; }
  100% { transform: translateY(-50%) translateX(0); opacity: 1; }
`;

const slideOutMobile = keyframes`
  0% { transform: translateY(-50%) translateX(0); opacity: 1; }
  100% { transform: translateY(-50%) translateX(120%); opacity: 0; }
`;

const FloatingTrustBadge: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [visible, setVisible] = useState(false);
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    const dismissed = sessionStorage.getItem(STORAGE_KEY);
    if (!dismissed) {
      const timer = setTimeout(() => setVisible(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    setClosing(true);
    setTimeout(() => {
      setVisible(false);
      sessionStorage.setItem(STORAGE_KEY, '1');
    }, 400);
  };

  const handleClick = () => {
    window.location.href = '/trust';
  };

  if (!visible || isMobile) return null;

  const seal = isMobile ? 80 : 100;
  const animIn = isMobile ? slideInMobile : slideInDesktop;
  const animOut = isMobile ? slideOutMobile : slideOutDesktop;

  return (
    <Box
      onClick={handleClick}
      sx={{
        position: 'fixed',
        top: '50%',
        zIndex: 9998,
        cursor: 'pointer',
        // 데스크탑: 오른쪽 여백(25%) 중앙 = 뷰포트 87.5% 지점
        // 모바일: 오른쪽 끝에 붙임
        ...(isMobile
          ? { right: 0, top: '65%', transform: 'translateY(-50%)' }
          : { left: '87.5%', transform: 'translate(-50%, -50%)' }
        ),
        animation: `${closing ? animOut : animIn} ${closing ? '0.4s' : '0.7s'} cubic-bezier(0.4, 0, 0.2, 1) forwards`,
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: isMobile ? 120 : 148,
          // 데스크탑: 여백 중앙이므로 모든 모서리 둥글게 / 모바일: 왼쪽만 둥글게
          borderRadius: isMobile ? '20px 0 0 20px' : '20px',
          background: 'rgba(255, 255, 255, 0.6)',
          backdropFilter: 'blur(12px)',
          boxShadow: '0 0 20px rgba(37, 99, 235, 0.12), inset 0 0 0 1px rgba(37, 99, 235, 0.10)',
          border: '1.5px solid rgba(37, 99, 235, 0.18)',
          py: isMobile ? 1.5 : 2,
          px: isMobile ? 1.2 : 1.5,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: isMobile ? 0.8 : 1,
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'scale(1.03)',
          },
        }}
      >
        {/* 닫기 */}
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{
            position: 'absolute',
            top: 6,
            right: 6,
            width: 24,
            height: 24,
            color: '#94a3b8',
            '&:hover': { color: '#475569', bgcolor: 'rgba(0,0,0,0.04)' },
          }}
        >
          <Close sx={{ fontSize: 16 }} />
        </IconButton>

        {/* 리본 메달 */}
        <svg width={isMobile ? 24 : 30} height={isMobile ? 30 : 38} viewBox="0 0 38 48" fill="none">
          <circle cx="19" cy="17" r="14" fill="url(#fbg)" stroke="#1e40af" strokeWidth="1.5" />
          <circle cx="19" cy="17" r="9.5" fill="none" stroke="rgba(147,197,253,0.5)" strokeWidth="1" />
          <path d="M13.5 17l4 4 7-8" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M12 29l-5 16 7-4.5L19 45V31" fill="#2563eb" />
          <path d="M26 29l5 16-7-4.5L19 45V31" fill="#1d4ed8" />
          <defs>
            <linearGradient id="fbg" x1="5" y1="3" x2="33" y2="31">
              <stop offset="0%" stopColor="#3b82f6" />
              <stop offset="100%" stopColor="#1e40af" />
            </linearGradient>
          </defs>
        </svg>

        {/* 타이틀 */}
        <Typography sx={{
          fontSize: isMobile ? '0.78rem' : '0.92rem',
          fontWeight: 900,
          color: '#0f172a',
          textAlign: 'center',
          lineHeight: 1.3,
          letterSpacing: '-0.03em',
          textShadow: '0 0.5px 0 rgba(0,0,0,0.08)',
        }}>
          중고폰 안심거래
          <br />
          <Box component="span" sx={{ color: '#1e40af' }}>인증 사업자</Box>
        </Typography>

        {/* ── 인증 씰 ── */}
        <Box
          sx={{
            width: seal,
            height: seal,
            borderRadius: '50%',
            border: '3px solid #1e40af',
            background: '#fff',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.4,
            p: 1,
            boxShadow: 'inset 0 0 0 3px rgba(30, 64, 175, 0.06)',
          }}
        >
          <Typography sx={{
            fontSize: isMobile ? '0.4rem' : '0.5rem',
            fontWeight: 800,
            color: '#2563eb',
            lineHeight: 1,
            letterSpacing: '0.03em',
          }}>
            한국정보통신진흥협회
          </Typography>

          <Box sx={{
            width: isMobile ? 20 : 26,
            height: isMobile ? 20 : 26,
            borderRadius: '6px',
            background: 'linear-gradient(135deg, #1e40af, #3b82f6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 2px 6px rgba(30, 64, 175, 0.25)',
          }}>
            <svg width={isMobile ? 11 : 14} height={isMobile ? 13 : 16} viewBox="0 0 18 20" fill="none">
              <rect x="2" y="0.5" width="14" height="17" rx="2" fill="none" stroke="#fff" strokeWidth="1.5" />
              <line x1="5.5" y1="5" x2="12.5" y2="5" stroke="#fff" strokeWidth="1" strokeLinecap="round" />
              <line x1="5.5" y1="8" x2="12.5" y2="8" stroke="#fff" strokeWidth="1" strokeLinecap="round" />
              <line x1="5.5" y1="11" x2="10" y2="11" stroke="#fff" strokeWidth="1" strokeLinecap="round" />
            </svg>
          </Box>

          <Typography sx={{
            fontSize: isMobile ? '0.36rem' : '0.44rem',
            fontWeight: 800,
            color: '#1e3a5f',
            textAlign: 'center',
            lineHeight: 1.25,
          }}>
            중고 단말 안심거래
            <br />인증 사업자
          </Typography>

          <Typography sx={{
            fontSize: isMobile ? '0.24rem' : '0.28rem',
            fontWeight: 600,
            color: '#64748b',
            lineHeight: 1,
          }}>
            Korea Assoc. for ICT Promotion
          </Typography>
        </Box>

        {/* 하단 CTA */}
        <Typography sx={{
          fontSize: isMobile ? '0.55rem' : '0.65rem',
          fontWeight: 700,
          color: '#2563eb',
          textAlign: 'center',
          lineHeight: 1.3,
        }}>
          자세히 보기 &rsaquo;
        </Typography>
      </Box>
    </Box>
  );
};

export default FloatingTrustBadge;
